import React from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';

import { useEcosystem } from '@/shared/contexts/EcosystemContext';

/**
 * SEO Component for managing page-specific meta tags and structured data.
 * Uses react-helmet-async for dynamic meta tag updates.
 *
 * Automatically handles:
 * - noindex for development/staging environments (via REACT_APP_NOINDEX)
 * - Canonical URLs pointing to production domain (rileconnect.org)
 * - Open Graph and Twitter Card meta tags
 * - JSON-LD structured data: BreadcrumbList, WebSite, Organization
 * - Tenant-aware defaults: page title suffix, default description,
 *   parentOrganization, and sameAs are derived from the active ecosystem
 *   when one is resolved (and from platform defaults on the apex).
 *
 * Props:
 * - breadcrumbs: Array of {name, path} for BreadcrumbList schema (enables
 *   Google sitelinks and breadcrumb display in search results)
 * - isHomePage: When true, emits WebSite + Organization schemas (enables
 *   Google Knowledge Panel and Sitelinks Search Box)
 * - jsonLd: Additional page-specific JSON-LD object to emit
 */

const PRODUCTION_URL = 'https://rileconnect.org';
const SITE_TITLE = 'RILE Connect';

// Apex-mode default description. Used when the platform pseudo-Ecosystem has
// no authored `metaDescription` (Wave M will add that schema field).
const APEX_DEFAULT_DESCRIPTION =
    'RILE Connect: an open platform for hosting learning ecosystems. ' +
    'Making pathways visible, navigable, and equitable from ' +
    'classroom to career.';

// Tenant-mode default description used when the active ecosystem has no
// authored description or metaDescription. Generic enough to read for any
// hosted ecosystem.
const tenantDefaultDescription = (ecosystemName) =>
    `${ecosystemName} on RILE Connect, making pathways visible, ` +
    `navigable, and equitable from classroom to career.`;

const SEO = ({
    title,
    description,
    canonical,
    image,
    type = 'website',
    noindex = false,
    breadcrumbs,
    isHomePage = false,
    jsonLd,
}) => {
    const { ecosystem, isPlatform } = useEcosystem();
    const shouldNoindex = noindex || process.env.REACT_APP_NOINDEX === 'true';

    // Schema-field caveat: `metaDescription` and `branding.parentOrg`,
    // `branding.socialLinks` are not yet on the Ecosystem schema (Wave M).
    // We read defensively so the UI degrades to sensible apex/tenant fallbacks
    // until those fields land.
    const ecoMetaDescription = ecosystem?.metaDescription;
    const ecoDescription = ecosystem?.description;
    const parentOrg = ecosystem?.branding?.parentOrg;
    const socialLinks = ecosystem?.branding?.socialLinks;
    const faviconUrl = ecosystem?.branding?.faviconUrl;

    // Effective title suffix.
    const titleSuffix = isPlatform
        ? `${SITE_TITLE} - RILE Connect Platform`
        : `${SITE_TITLE} - ${ecosystem?.name || 'RILE Connect'}`;
    // A caller passing the bare site title (e.g. the apex home page) would
    // otherwise render "RILE Connect | RILE Connect" in the <title>,
    // og:title, and twitter:title — skip the suffix in that case.
    const fullTitle =
        title && title !== SITE_TITLE
            ? `${title} | ${SITE_TITLE}`
            : title || titleSuffix;

    // Effective default description.
    const defaultDescription = isPlatform
        ? ecoMetaDescription || APEX_DEFAULT_DESCRIPTION
        : ecoMetaDescription ||
          ecoDescription ||
          tenantDefaultDescription(ecosystem?.name || 'This ecosystem');
    const metaDescription = description || defaultDescription;
    // Open Graph / Twitter image tags must be absolute URLs — LinkedIn and
    // Twitter's inspectors don't resolve a root-relative path against the
    // page URL themselves. Callers may pass either form.
    const metaImage = image
        ? image.startsWith('http')
            ? image
            : `${PRODUCTION_URL}${image}`
        : '';

    const canonicalUrl = canonical
        ? `${PRODUCTION_URL}${canonical}`
        : typeof window !== 'undefined'
          ? `${PRODUCTION_URL}${window.location.pathname}`
          : PRODUCTION_URL;

    // --- JSON-LD Structured Data ---
    const structuredData = [];

    // BreadcrumbList: enables breadcrumb display in Google search results
    if (breadcrumbs?.length) {
        structuredData.push({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((crumb, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                name: crumb.name,
                item: `${PRODUCTION_URL}${crumb.path}`,
            })),
        });
    }

    // WebSite: enables Sitelinks Search Box in Google results
    if (isHomePage) {
        structuredData.push({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: SITE_TITLE,
            url: PRODUCTION_URL,
            description: defaultDescription,
            potentialAction: {
                '@type': 'SearchAction',
                target: {
                    '@type': 'EntryPoint',
                    urlTemplate: `${PRODUCTION_URL}/directory?q={search_term_string}`,
                },
                'query-input': 'required name=search_term_string',
            },
        });

        // Organization: establishes identity for Knowledge Panel.
        // parentOrganization and sameAs are derived from the active ecosystem
        // when authored; omitted entirely otherwise (avoid fabricating an
        // affiliation a tenant may not have).
        const orgRecord = {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: isPlatform ? SITE_TITLE : ecosystem?.name || SITE_TITLE,
            url: PRODUCTION_URL,
            description: defaultDescription,
        };

        if (parentOrg && parentOrg.name) {
            orgRecord.parentOrganization = {
                '@type': 'CollegeOrUniversity',
                name: parentOrg.name,
                ...(parentOrg.url ? { url: parentOrg.url } : {}),
            };
        }

        const sameAs = socialLinks
            ? Object.values(socialLinks).filter(
                  (v) => typeof v === 'string' && v
              )
            : [];
        if (sameAs.length > 0) {
            orgRecord.sameAs = sameAs;
        }

        structuredData.push(orgRecord);
    }

    // Page-specific structured data
    if (jsonLd) {
        structuredData.push(jsonLd);
    }

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="title" content={fullTitle} />
            <meta name="description" content={metaDescription} />

            {/* Canonical URL */}
            <link rel="canonical" href={canonicalUrl} />

            {/* Per-tenant favicon override (falls back to the platform default
                favicon from index.html when the ecosystem has none authored) */}
            {faviconUrl && <link rel="icon" href={faviconUrl} />}

            {/* Robots */}
            {shouldNoindex ? (
                <meta name="robots" content="noindex, nofollow" />
            ) : (
                <meta name="robots" content="index, follow" />
            )}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={metaDescription} />
            {metaImage && <meta property="og:image" content={metaImage} />}
            <meta property="og:site_name" content={SITE_TITLE} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={metaDescription} />
            {metaImage && <meta name="twitter:image" content={metaImage} />}

            {/* JSON-LD Structured Data */}
            {structuredData.map((data, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(data)}
                </script>
            ))}
        </Helmet>
    );
};

SEO.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    canonical: PropTypes.string,
    image: PropTypes.string,
    type: PropTypes.string,
    noindex: PropTypes.bool,
    breadcrumbs: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            path: PropTypes.string.isRequired,
        })
    ),
    isHomePage: PropTypes.bool,
    jsonLd: PropTypes.object,
};

export default SEO;
